import {
  AutomationActionTemplateGoogleCalendarEventAnchor,
  CreateMeetingServiceType,
  CREATE_MEETING_SERVICES,
  IExperienceTemplateResource,
  ISubtaskCreateMeetingTemplate,
  StorageContext
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  Chip,
  FormHelperText,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  useTheme
} from '@material-ui/core';
import { capitalize, compact } from 'lodash';
import moment from 'moment-timezone';
import { SetStateAction, useMemo } from 'react';
import {
  AutocompleteMulti,
  AutocompleteMultiFreeSolo
} from '../../../../../../../../components/Autocomplete';
import { DataFieldWithLabel } from '../../../../../../../../components/DataFieldWithLabel';
import { FormRow } from '../../../../../../../../components/Form/FormRow';
import {
  Token,
  TokenDefinition
} from '../../../../../../../../components/MarkdownText';
import { SelectCustom } from '../../../../../../../../components/SelectCustom';
import { TextFieldWithReplacementTokens } from '../../../../../../../../components/TextFieldWithReplacementTokens';
import { useUnsafeTranslation } from '../../../../../../../../i18next';
import { FlexContainer } from '../../../../../../../../layout/Flex';
import {
  ONE_MINUTE,
  printTz,
  toWeekAndDayDuration
} from '../../../../../../../../services/time';
import { getUsersByOrgId } from '../../../../../../../../services/user';
import { AnchorField } from '../../../../../Anchors';
import { useGoogleCalendarEventInvitiationAnchorOptions } from '../../../../AutomationBuilder/ActionSettings/GoogleCalendar';
import {
  ActionActorSelectorMulti,
  actionActorToString
} from '../../../../AutomationBuilder/shared';
import { BlockSettingsSubPage, TaskBuilderContext } from '../../../types';
import {
  TaskBuilderBlockValidationState,
  TaskBuilderBlockValidationStateItem,
  useFieldValidator,
  VALIDATORS_CREATE_CALENDAR_EVENT
} from '../../../validation';
import { DaySelector, RepeatSelector, TimePicker } from '../../TimeSelectors';
import { OnCompleteHooks, OnCompleteHooksReadOnly } from './onCompleteHooks';

export const BlockSettingsSubtaskCreateCalendarEvent = ({
  value,
  validation,
  expTpl,
  setValidation,
  onChange,
  tokens,
  anchorFields,
  resources,
  onChangeResources,
  goToSubPage,
  customFields,
  showOnCompleteHookSettings
}: {
  value: ISubtaskCreateMeetingTemplate;
  onChange: (nextValue: ISubtaskCreateMeetingTemplate) => void;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  tokens: TokenDefinition[];
  anchorFields: AnchorField[];
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
  showOnCompleteHookSettings: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();

  const validateHooks = useFieldValidator(
    setValidation,
    VALIDATORS_CREATE_CALENDAR_EVENT,
    'hooks'
  );

  const invalidPpltoAdd = validation?.fields.pplToAdd || null;
  const invalidEventTitle = validation?.fields.title || null;
  const invalidAnchor = validation?.fields.anchor || null;
  const invalidEventDescription = validation?.fields.description || null;
  const invalidCreateLinksFor = validation?.fields.createLinksFor || null;

  const validatePplToAdd = useFieldValidator(
    setValidation,
    VALIDATORS_CREATE_CALENDAR_EVENT,
    'pplToAdd'
  );

  const validateEventTitle = useFieldValidator(
    setValidation,
    VALIDATORS_CREATE_CALENDAR_EVENT,
    'title'
  );

  const validateEventDescription = useFieldValidator(
    setValidation,
    VALIDATORS_CREATE_CALENDAR_EVENT,
    'description'
  );

  const validateAnchor = useFieldValidator(
    setValidation,
    VALIDATORS_CREATE_CALENDAR_EVENT,
    'anchor'
  );

  const validateCreateLinksFor = useFieldValidator(
    setValidation,
    VALIDATORS_CREATE_CALENDAR_EVENT,
    'createLinksFor'
  );

  return (
    <div>
      <FormRow>
        <AutocompleteMulti
          label={t('Choose your calendar')}
          fullWidth
          value={value.data.createLinksFor}
          options={Object.values(CREATE_MEETING_SERVICES).map((a) => a.type)}
          getOptionLabel={(a) => CREATE_MEETING_SERVICES[a].name}
          onChange={(nextCreateLinks) => {
            const nextValue = {
              ...value,
              data: {
                ...value.data,
                createLinksFor: nextCreateLinks
              }
            };
            invalidCreateLinksFor && validateCreateLinksFor(nextValue, expTpl);
            return onChange(nextValue);
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="default"
                size="small"
                label={CREATE_MEETING_SERVICES[option].name}
                {...getTagProps({ index })}
              />
            ))
          }
          error={!!invalidCreateLinksFor}
          helperText={invalidCreateLinksFor}
          onBlur={() => validateCreateLinksFor(value, expTpl)}
        />
      </FormRow>
      <FormRow>
        <ActionActorSelectorMulti
          label={t('Who should be invited')}
          value={value.data.pplToAdd.filter((p) => p.type === 'actor')}
          onChange={(nextPplToAdd) => {
            const nextValue = {
              ...value,
              data: {
                ...value.data,
                pplToAdd: nextPplToAdd.concat(
                  value.data.pplToAdd.filter((p) => p.type === 'other')
                )
              }
            };
            invalidPpltoAdd && validatePplToAdd(nextValue, expTpl);
            return onChange(nextValue);
          }}
          expTpl={expTpl}
          fullWidth
          error={!!invalidPpltoAdd}
          helperText={invalidPpltoAdd}
          onBlur={() => validatePplToAdd(value, expTpl)}
        />
      </FormRow>
      <FormRow>
        <AutocompleteMultiFreeSolo
          label={t('Additional people to invite (by email)')}
          fullWidth
          value={compact(
            value.data.pplToAdd.map((p) =>
              p.type === 'other' ? p.email : null
            )
          )}
          tokens={tokens}
          allowTokens={true}
          onChange={(nextEmails) => {
            const nextPplToAdd = value.data.pplToAdd
              .filter((p) => p.type === 'actor')
              .concat(
                nextEmails.map((email) => ({
                  type: 'other',
                  email: email.trim(),
                  optional: false
                }))
              );
            const nextValue = {
              ...value,
              data: { ...value.data, pplToAdd: nextPplToAdd }
            };
            invalidPpltoAdd && validatePplToAdd(nextValue, expTpl);
            return onChange(nextValue);
          }}
          options={() =>
            getUsersByOrgId(expTpl.orgId).then((us) =>
              us.map((u) => u.data.email).filter((t) => t)
            )
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="default"
                size="small"
                label={option}
                {...getTagProps({ index })}
              />
            ))
          }
          onBlur={() => validatePplToAdd(value, expTpl)}
        />
      </FormRow>
      <FlexContainer alignItems="baseline" marginBottom={1}>
        <Typography variant="h5" gutterBottom>
          {t('Event Information')}
        </Typography>
      </FlexContainer>
      <FormRow>
        <TextFieldWithReplacementTokens
          label={t('Event title')}
          value={value.data.title}
          fullWidth
          variant="outlined"
          error={!!invalidEventTitle}
          helperText={invalidEventTitle}
          onBlur={() => validateEventTitle(value, expTpl)}
          onChange={(title) => {
            const nextValue = {
              ...value,
              data: {
                ...value.data,
                title
              }
            };
            invalidEventTitle && validateEventTitle(nextValue, expTpl);
            onChange(nextValue);
          }}
          tokens={tokens}
        />
      </FormRow>
      <FormRow>
        <TextFieldWithReplacementTokens
          label={t('Event description')}
          value={value.data.description}
          fullWidth
          multiline
          variant="outlined"
          style={{ alignItems: 'flex-start' }}
          error={!!invalidEventDescription}
          helperText={invalidEventDescription}
          onBlur={() => validateEventDescription(value, expTpl)}
          onChange={(description) => {
            const nextValue = {
              ...value,
              data: {
                ...value.data,
                description
              }
            };
            invalidEventDescription &&
              validateEventDescription(nextValue, expTpl);
            onChange(nextValue);
          }}
          tokens={tokens}
        />
      </FormRow>
      <FlexContainer alignItems="baseline" marginBottom={1}>
        <Typography variant="h5" gutterBottom>
          {t('Suggested Date')}
        </Typography>
      </FlexContainer>
      <FormRow>
        <DaySelector
          value={value.data.anchor.lowerBoundary}
          zeroBased={true}
          onChange={(nextLowerBoundary: number) => {
            const nextValue = { ...value };
            nextValue.data.anchor.lowerBoundary = nextLowerBoundary;
            return onChange(nextValue);
          }}
        />
        <TimePicker
          t={value.data.at}
          minuteStep={10}
          onChange={(nextTime) => {
            const nextVal = { ...value };
            nextVal.data.at.hour = nextTime.hour;
            nextVal.data.at.minute = nextTime.minute;
            return onChange(nextVal);
          }}
          tz={expTpl.tz}
          autoWidth
        />
      </FormRow>
      <AnchorSelector
        value={value.data.anchor}
        onChange={(nextAnchor) => {
          const nextValue: ISubtaskCreateMeetingTemplate = { ...value };
          nextValue.data.anchor = nextAnchor;
          invalidAnchor && validateAnchor(value, expTpl);
          return onChange(nextValue);
        }}
        expTpl={expTpl}
        error={!!invalidAnchor}
        fullWidth
        onBlur={() => validateAnchor(value, expTpl)}
        anchorFields={anchorFields}
      />
      <FormHelperText
        error={!!invalidAnchor}
        style={{ marginBottom: theme.spacing(1) }}
      >
        {invalidAnchor}
      </FormHelperText>
      <FormRow>
        <FlexContainer marginTop={1} fullWidth>
          <TextField
            value={Math.floor(value.data.at.duration / ONE_MINUTE)}
            type="number"
            label={t('Meeting Duration')}
            variant="outlined"
            fullWidth
            inputProps={{
              min: 5,
              step: '5'
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">{t('Minutes')}</InputAdornment>
              )
            }}
            onChange={(ev) => {
              const nextValue = +ev.target.value;
              const nextVal = { ...value };
              nextVal.data.at.duration = nextValue * ONE_MINUTE;
              return onChange(nextVal);
            }}
          />
        </FlexContainer>
      </FormRow>
      <RepeatSelector
        value={value.data.repeat}
        disabled={value.data.createLinksFor.includes(
          CreateMeetingServiceType.MS_OUTLOOK_CALENDAR
        )}
        onChange={(repeat) => {
          onChange({
            ...value,
            data: {
              ...value.data,
              repeat
            }
          });
        }}
      />

      <OnCompleteHooks
        value={value.hooks.onComplete}
        onChange={(nextHooks) => {
          const nextValue = {
            ...value,
            hooks: {
              ...value.hooks,
              onComplete: nextHooks
            }
          };
          validateHooks(nextValue, expTpl);
          onChange(nextValue);
        }}
        tokens={tokens}
        resources={resources}
        onChangeResources={onChangeResources}
        expTpl={expTpl}
        customFields={customFields}
        goToSubPage={goToSubPage}
        validation={validation}
        showOnCompleteHookSettings={showOnCompleteHookSettings}
        storageContext={StorageContext.SUBTASK}
      />
    </div>
  );
};

export const BlockSettingsSubtaskCreateCalendarEventReadOnly = ({
  value,
  expTpl,
  anchorFields,
  showOnCompleteHookSettings,
  tokens,
  customFields,
  resources
}: {
  value: ISubtaskCreateMeetingTemplate;
  expTpl: TaskBuilderContext;
  anchorFields: AnchorField[];
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
  customFields: CustomUserField[];
  resources: IExperienceTemplateResource[];
}) => {
  const { t } = useUnsafeTranslation();
  const options = useGoogleCalendarEventInvitiationAnchorOptions(
    expTpl.anchor,
    anchorFields
  );

  const { tz } = expTpl;

  const suggestedDate = useMemo(() => {
    const anchor =
      options.find((o) => o.type === value.data.anchor.type)?.label || '';
    const onAnchor = capitalize(anchor.toLowerCase().replace('after', 'on'));
    const time = moment()
      .hour(value.data.at.hour)
      .minute(value.data.at.minute)
      .format('hh:mm a');
    const atTime = `at ${time} ${printTz(tz)}`;
    let finalString =
      `${toWeekAndDayDuration(value.data.anchor.lowerBoundary, t)} ${
        value.data.anchor.lowerBoundary === 0 ? onAnchor : anchor
      } ${atTime}` || `${capitalize(anchor)} ${atTime}`;
    finalString = finalString.trim();
    return finalString.length
      ? finalString[0].toUpperCase() + finalString.slice(1)
      : finalString;
  }, [value, options, tz, t]);

  return (
    <>
      <FormRow>
        <Typography variant="h6" component="h6">
          {t('Who should be invited')}
        </Typography>
      </FormRow>
      <FlexContainer wrap={'wrap'}>
        {value.data.pplToAdd.map((p) => (
          <Token color="blue" key={p.type === 'actor' ? p.key : p.email}>
            {actionActorToString(p, expTpl.supportingActors, t)}
          </Token>
        ))}
      </FlexContainer>
      <br />
      <FormRow>
        <Typography variant="h6" component="h6">
          {t('Event Information')}
        </Typography>
      </FormRow>
      <FormRow>
        <DataFieldWithLabel label={capitalize(t('subTask.createMeeting.verb'))}>
          {value.data.title}
        </DataFieldWithLabel>
      </FormRow>
      <FormRow>
        <DataFieldWithLabel
          label={capitalize(t('subTask.createMeeting.eventDescription'))}
        >
          {value.data.description}
        </DataFieldWithLabel>
      </FormRow>
      <FormRow>
        <DataFieldWithLabel label={t('Suggested Date')}>
          <div>{suggestedDate}</div>
          {value.data.repeat.enabled && (
            <div>
              {t('Repeat {{ frequency }}', {
                frequency: t(value.data.repeat.frequency.toLowerCase())
              })}
            </div>
          )}
        </DataFieldWithLabel>
      </FormRow>

      {showOnCompleteHookSettings && (
        <OnCompleteHooksReadOnly
          value={value.hooks.onComplete}
          resources={resources}
          expTpl={expTpl}
          customFields={customFields}
          tokens={tokens}
          storageContext={StorageContext.SUBTASK}
        />
      )}
    </>
  );
};

const AnchorSelector = ({
  value,
  onChange,
  expTpl,
  fullWidth,
  anchorFields,
  error,
  onBlur
}: {
  value: AutomationActionTemplateGoogleCalendarEventAnchor;
  onChange: (
    nextAnchor: AutomationActionTemplateGoogleCalendarEventAnchor
  ) => void;
  expTpl: TaskBuilderContext;
  fullWidth?: boolean;
  error?: boolean;
  anchorFields: AnchorField[];
  onBlur: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const options = useGoogleCalendarEventInvitiationAnchorOptions(
    expTpl.anchor,
    anchorFields
  ).filter((op) => op.type !== 'AFTER_AUTOMATION');
  return (
    <>
      <FormRow style={{ marginBottom: 0 }}>
        <SelectCustom
          label={t('Relative to')}
          fullWidth={fullWidth}
          value={value.type}
          onChange={(ev) => {
            const nextType = ev.target
              .value as AutomationActionTemplateGoogleCalendarEventAnchor['type'];
            const nextValue = options.find((o) => o.type === nextType)?.data;
            if (nextValue) {
              onChange({
                ...nextValue,
                lowerBoundary: value.lowerBoundary
              });
            }
          }}
        >
          {options.map((o) => (
            <MenuItem key={o.type} value={o.type} disabled={o.disabled}>
              {capitalize(o.label)}
            </MenuItem>
          ))}
        </SelectCustom>
      </FormRow>
    </>
  );
};
